import { Dtos, SuretyAccountStatus } from "@inrev/common";
import {
	useCreateWorkInProgressStatement,
	useFetchAccountLatestWipStatement,
	useFetchAccountWorkInProgressStatements,
	useFetchSuretyAccount,
	useUpdateWorkInProgressStatement,
} from "@portal/api/account";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import {
	type WIPFormOutput,
	WIPStatementCard,
	WIPStatementModal,
} from "@portal/components/features/wip/wip-statement";
import { Button, Icon, PartialIndicator, Text } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { MutationErrorFlag } from "@portal/components/ui/MutationErrorFlag";
import { TimeLine, TimeLineProps } from "@portal/components/ui/TimeLine";
import { DateTime } from "luxon";
import { FC, useMemo, useState } from "react";
import { AccountTabProps } from "../../account/AccountLayout";
import { SectionHeaderWithAdd } from "../../account/admin/tabs/details/SectionHeaderWithAdd";
import { checkWip } from "./AccountOverviewTab/utils/getMissingSections";

type AccountWIPTabProps = AccountTabProps;

type ModalState =
	| { mode: "closed" }
	| { mode: "create"; disableDatePickerDays: string[] }
	| { mode: "edit"; wipStatement: Dtos.WipStatement.Get.Response };

export const AccountWIPTab: FC<AccountWIPTabProps> = ({ accountId }) => {
	const { suretyAccount, suretyAccountError, refetchSuretyAccount } =
		useFetchSuretyAccount(accountId);
	const { wipStatements, wipStatementsIsLoading, wipStatementsError, refetchWipStatements } =
		useFetchAccountWorkInProgressStatements(accountId);
	const { latestWipStatement } = useFetchAccountLatestWipStatement(accountId);
	const {
		createWipStatement,
		createWipStatementIsLoading,
		createWipStatementError,
		resetCreateWipStatementError,
	} = useCreateWorkInProgressStatement(accountId);
	const {
		updateWipStatement,
		updateWipStatementIsLoading,
		updateWipStatementError,
		resetUpdateWipStatementError,
	} = useUpdateWorkInProgressStatement(accountId);
	const [modalState, setModalState] = useState<ModalState>({ mode: "closed" });

	const sorted = useMemo(
		() =>
			wipStatements
				? [...wipStatements].sort(
						(a, b) => DateTime.fromISO(b.date).toMillis() - DateTime.fromISO(a.date).toMillis(),
					)
				: [],
		[wipStatements],
	);

	const isAccountClosed = suretyAccount?.status === SuretyAccountStatus.closed;
	const isMutating = createWipStatementIsLoading || updateWipStatementIsLoading;
	const partialActive = suretyAccount?.status === SuretyAccountStatus.review;
	const sectionPartial =
		suretyAccount?.modelSize != null &&
		checkWip(latestWipStatement, suretyAccount.modelSize) !== null;
	const hasStatements = sorted.length > 0;
	const canAdd = !isAccountClosed;

	if (suretyAccountError) {
		return (
			<ReportedRetryableLoadError
				error={suretyAccountError}
				resource="account"
				onRetry={refetchSuretyAccount}
			/>
		);
	}

	if (wipStatementsError) {
		return (
			<ReportedRetryableLoadError
				error={wipStatementsError}
				resource="work in progress statements"
				onRetry={refetchWipStatements}
			/>
		);
	}

	if (wipStatementsIsLoading || !wipStatements || !suretyAccount) {
		return <CenteredSpinner />;
	}

	const timelineItems: TimeLineProps["items"] = sorted.map((wipStatement, index) => {
		const dt = DateTime.fromISO(wipStatement.date);
		return {
			id: wipStatement.id,
			label: (
				<Text type="caption" color="tertiary" className="w-[28px]">
					{dt.toFormat("MMM yyyy")}
				</Text>
			),
			anchorOffsetTop: 25,
			el: (
				<WIPStatementCard
					wipStatement={wipStatement}
					partialActive={partialActive}
					modelSize={suretyAccount.modelSize}
					alwaysExpanded={index === 0}
					disabled={isMutating}
					readOnly={isAccountClosed}
					onEditClick={
						isAccountClosed || wipStatement.underwriterLock
							? undefined
							: () => setModalState({ mode: "edit", wipStatement })
					}
					onFilesChange={
						isMutating
							? undefined
							: (files) => {
									updateWipStatement({
										wipId: wipStatement.id,
										data: {
											projectBacklogCost: wipStatement.projectBacklogCost ?? undefined,
											projectBacklogGrossProfit:
												wipStatement.projectBacklogGrossProfit ?? undefined,
											fileIds: files.map((f) => f.id),
										},
									});
								}
					}
				/>
			),
		};
	});

	if (!hasStatements) {
		timelineItems.push({
			id: "empty",
			el: (
				<Text type="caption" color="tertiary" className="italic">
					No Work In Progress Statements
				</Text>
			),
		});
	}

	const handleCreate = () => {
		const disableDatePickerDays = sorted.map((wip) => wip.date);
		setModalState({ mode: "create", disableDatePickerDays });
	};

	const handleCreateSubmit = async (data: WIPFormOutput) => {
		try {
			await createWipStatement({
				date: data.date,
				companyContactId: suretyAccount.primaryCompanyId,
				projectBacklogCost: data.projectBacklogCost,
				projectBacklogGrossProfit: data.projectBacklogGrossProfit,
				fileIds: data.files.map((f) => f.id),
			});
			setModalState({ mode: "closed" });
		} catch {}
	};

	const handleEditSubmit = async (data: WIPFormOutput) => {
		if (modalState.mode !== "edit") return;
		try {
			await updateWipStatement({
				wipId: modalState.wipStatement.id,
				data: {
					projectBacklogCost: data.projectBacklogCost,
					projectBacklogGrossProfit: data.projectBacklogGrossProfit,
					fileIds: data.files.map((f) => f.id),
				},
			});
			setModalState({ mode: "closed" });
		} catch {}
	};

	return (
		<div className="relative flex flex-col w-full pb-[125px]" data-testid="agent-account-wip-tab">
			<div className="flex flex-col space-y-5">
				<div className="flex items-center justify-between w-full">
					<div className="flex items-center gap-x-2">
						{hasStatements && canAdd ? (
							<SectionHeaderWithAdd
								title="Work In Progress"
								testId="agent-add-wip-statement-button"
								addLabel="Add WIP Statement"
								onAdd={handleCreate}
							/>
						) : (
							<Text type="heading" color="primary">
								Work In Progress
							</Text>
						)}
						{sectionPartial && (
							<PartialIndicator active={partialActive} testId="agent-account-wip-partial" />
						)}
					</div>
					{!hasStatements && canAdd && (
						<Button
							variant="outline"
							color="secondary"
							size="sm"
							className="w-fit"
							data-testid="agent-add-wip-statement-button"
							leadingIcon={<Icon type="plus" size="micro" color="secondary" />}
							onClick={handleCreate}
							disabled={isMutating}
						>
							Add a Work In Progress Statement
						</Button>
					)}
				</div>
				<TimeLine items={timelineItems} />
			</div>
			{modalState.mode === "create" && (
				<WIPStatementModal
					mode="create"
					disableDatePickerDays={modalState.disableDatePickerDays}
					onClose={() => setModalState({ mode: "closed" })}
					onSubmit={handleCreateSubmit}
				/>
			)}
			{modalState.mode === "edit" && (
				<WIPStatementModal
					mode="edit"
					defaultValues={{
						date: modalState.wipStatement.date,
						projectBacklogCost:
							modalState.wipStatement.projectBacklogCost != null
								? String(modalState.wipStatement.projectBacklogCost)
								: undefined,
						projectBacklogGrossProfit:
							modalState.wipStatement.projectBacklogGrossProfit != null
								? String(modalState.wipStatement.projectBacklogGrossProfit)
								: undefined,
						files: modalState.wipStatement.files,
					}}
					readOnlyDate
					onClose={() => setModalState({ mode: "closed" })}
					onSubmit={handleEditSubmit}
				/>
			)}
			<MutationErrorFlag
				errorHeading="Error Creating WIP Statement"
				mutationError={createWipStatementError}
				dismissError={resetCreateWipStatementError}
			/>
			<MutationErrorFlag
				errorHeading="Error Updating WIP Statement"
				mutationError={updateWipStatementError}
				dismissError={resetUpdateWipStatementError}
			/>
		</div>
	);
};
